import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import Input from '../../../../components/form/input/Input';
import {
  createSubcontractorAccount,
  editSubcontractorAccount,
  getExternalUsersByProjectId
} from '../../../../core/state/subcontractors/asyncActions';
import { Color } from '../../../../core/enums/Color';
import { addSubcontractorToProject, validateSubcontractorAccount } from '../../../../core/state/project/asyncActions';
import { SubcontractorAccount } from '../../../../core/state/project/types';
import { PHONE_NUMBER_REGEX } from '../../../../core/constants/regex';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { ExternalUserStatus } from '../../../../core/enums/ExternalUserStatus';
import { RootState } from '../../../../core/state/state';

import './ManageExternalUserModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  isEditExternal?: boolean;
  selectedSubcontractor?: number;
}

type EditUserStatusFormData = {
  email: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  phoneNumber: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactPhoneNumber: string;
  notificationsEmail: string;
  notificationsContactName: string;
};

const defaultFormValues = {
  email: '',
  name: '',
  address: '',
  postalCode: '',
  city: '',
  phoneNumber: '',
  primaryContactFirstName: '',
  primaryContactLastName: '',
  primaryContactPhoneNumber: '',
  notificationsEmail: '',
  notificationsContactName: ''
};

const ManageExternalUserModal: React.FC<Props> = ({ isOpen, closeModal, isEditExternal = false, selectedSubcontractor }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const [subcontractorId, setSubcontractorId] = React.useState<number | null>(null);
  const [step, setStep] = React.useState<number>(isEditExternal ? 2 : 1);
  const user = useSelector((state: RootState) => state.subcontractor.projectSubcontractors?.find((x) => x.id === selectedSubcontractor));
  const disabled = subcontractorId !== null && !isEditExternal;

  const getModalTitle = () => {
    switch (step) {
      case 1:
        return t('modal.manageExternalUserModal.title.validateAccount');
      case 2:
        if (subcontractorId || isEditExternal) {
          return t(`modal.manageExternalUserModal.title.${isEditExternal ? 'editUser' : 'addUser'}`);
        }
        return t('modal.manageExternalUserModal.title.createNew');
      default:
        return t('modal.manageExternalUserModal.title.validateAccount');
    }
  };

  const validationSchema = yup
    .object()
    .shape({
      email: yup.string().required(t('validation.email.required')).email(t('validation.email.invalidFormat')).min(2),
      notificationsContactName: yup.string(),
      name: yup.string().required(t('validation.generic.required')),
      address: yup.string().required(t('validation.generic.required')),
      postalCode: yup
        .string()
        .required()
        .matches(/^[0-9]+$/, t('validation.onlyNumbers'))
        .min(5, t('validation.fiveDigits'))
        .max(5, t('validation.fiveDigits')),
      city: yup.string().required(t('validation.generic.required')),
      phoneNumber: yup.string().required(t('validation.generic.required')).matches(PHONE_NUMBER_REGEX, t('validation.phone')),
      primaryContactFirstName: yup.string().required(t('validation.generic.required')),
      primaryContactLastName: yup.string().required(t('validation.generic.required')),
      primaryContactPhoneNumber: yup.string().required(t('validation.generic.required')).matches(PHONE_NUMBER_REGEX, t('validation.phone')),
      notificationsEmail: yup.string().email(t('validation.email.invalidFormat'))
    })
    .required();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    getValues,
    setValue,
    reset
  } = useForm<EditUserStatusFormData>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(
      !subcontractorId ? validationSchema : yup.object().shape({ notificationsEmail: yup.string().email(t('validation.email.invalidFormat')) })
    )
  });

  const validateSubcontractorEmail = async () => {
    const notificationsEmail = getValues('notificationsEmail');
    const email = isEditExternal ? user?.email : getValues('email');
    if (email) {
      const response: SubcontractorAccount | null | false = await dispatch<any>(
        validateSubcontractorAccount(Number(projectId), email, isEditExternal)
      );
      if (response) {
        setSubcontractorId(response.subcontractor.id);
        setValue('email', email);
        setValue('notificationsContactName', response.subcontractor.notificationsContactName ?? '');
        setValue('notificationsEmail', response.subcontractor.notificationsEmail ?? '');
        setValue('name', response.subcontractor.name ?? '');
        setValue('address', response.subcontractor.address ?? '');
        setValue('postalCode', response.subcontractor.postalCode ?? '');
        setValue('city', response.subcontractor.city ?? '');
        setValue('phoneNumber', response.subcontractor.phoneNumber ?? '');
        setValue('primaryContactFirstName', response.primaryContactDetails.firstName ?? '');
        setValue('primaryContactLastName', response.primaryContactDetails.lastName ?? '');
        setValue('primaryContactPhoneNumber', response.primaryContactDetails.phoneNumber ?? '');
        setStep(isEditExternal ? step : step + 1);
      } else if (!errors.email?.message && response !== false) {
        reset(defaultFormValues);
        setValue('email', email);
        setValue('notificationsEmail', notificationsEmail ?? '');
        setStep(isEditExternal ? step : step + 1);
      } else {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Info,
            title: t('externalUsers.subcontractorRoles.activeSubcontractor'),
            message: t('modal.manageExternalUserModal.form.message.user')
          })
        );
      }
    }
  };

  useEffect(() => {
    if (isEditExternal) {
      validateSubcontractorEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (formData: EditUserStatusFormData) => {
    if (isEditExternal && subcontractorId) {
      await dispatch(
        editSubcontractorAccount(
          {
            ...formData,
            projectId: Number(projectId)
          },
          subcontractorId
        )
      );
    } else if (subcontractorId) {
      await dispatch(addSubcontractorToProject(Number(projectId), subcontractorId, formData.notificationsEmail, formData.notificationsContactName));
    } else {
      await dispatch(
        createSubcontractorAccount({
          ...formData,
          projectId: Number(projectId)
        })
      );
    }
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: t(`modal.${isEditExternal ? 'editExternalUser' : 'createExternalUser'}.form.message.success`),
        title: isEditExternal ? t('common.notification.success') : undefined
      })
    );
    closeModal();
    dispatch(
      getExternalUsersByProjectId(Number(projectId), [
        ExternalUserStatus.Active,
        ExternalUserStatus.Locked,
        ExternalUserStatus.Invited,
        ExternalUserStatus.Unblocked
      ])
    );
  };

  const buttons = (
    <div className="modal-buttons">
      {step === 2 && !isEditExternal && (
        <Button
          className="modal-buttons__left"
          color={Color.Blue}
          buttonTypes={['blue-outline']}
          onClick={() => {
            setSubcontractorId(null);
            setStep(step - 1);
          }}
        >
          <span>{t('common.back')}</span>
        </Button>
      )}
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      {step === 1 ? (
        <Button disabled={!isDirty} onClick={() => validateSubcontractorEmail()} buttonTypes={['blue']}>
          {t('common.next')}
        </Button>
      ) : (
        <Button disabled={!subcontractorId ? !isValid : false} form="manage-external-user" type="submit" buttonTypes={['blue']}>
          {subcontractorId ? t(`common.${isEditExternal ? 'update' : 'add'}`) : t('common.create')}
        </Button>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={<h3 className="modal-title">{getModalTitle()}</h3>} toolbar={buttons} size="small">
      <form id="manage-external-user" className="manage-external-user-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="manage-external-user-modal__content">
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                type="string"
                inputMode="email"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="email"
                error={errors.email?.message}
                name={name}
                label={t('common.email')}
                disabled={step !== 1 || subcontractorId !== null}
                infoText={`* (${t('common.forLogin')})`}
                placeholder={t('common.placeholder.email')}
                value={value}
              />
            )}
          />

          {step === 2 && (
            <>
              <Controller
                name="name"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <Input
                    label={t('modal.manageExternalUserModal.form.subcontractorName')}
                    placeholder={t('modal.manageExternalUserModal.form.placeholder.name')}
                    id="name"
                    error={errors.name?.message}
                    name={name}
                    disabled={disabled}
                    type="string"
                    required
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
              <Controller
                name="notificationsContactName"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="string"
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    id="notificationsContactName"
                    error={errors.notificationsContactName?.message}
                    name="notificationsContactName"
                    label={`${t('common.contactPerson')} (${t('common.forNotifications')})`}
                    placeholder={t('common.name')}
                    value={value}
                  />
                )}
              />
              <Controller
                name="notificationsEmail"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <Input
                    type="string"
                    inputMode="email"
                    onChange={(e) => onChange(e.target.value)}
                    id="notificationsEmail"
                    error={errors.notificationsEmail?.message}
                    name={name}
                    infoText={` (${t('common.forNotifications')})`}
                    label={t('common.email')}
                    placeholder={t('common.placeholder.email')}
                    value={value}
                  />
                )}
              />
              <div className="d-flex justify-content-between">
                <Controller
                  name="address"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Input
                      className="w-100 pr-3"
                      label={t('common.address')}
                      placeholder={t('modal.manageExternalUserModal.form.placeholder.streetName')}
                      id="address"
                      error={errors.address?.message}
                      disabled={disabled}
                      name={name}
                      type="string"
                      required
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                />
                <Controller
                  name="postalCode"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Input
                      className="w-100"
                      label={t('common.postalCode')}
                      placeholder={t('common.postalCode')}
                      id="postal-code"
                      error={errors.postalCode?.message}
                      name={name}
                      disabled={disabled}
                      type="number"
                      required
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                />
              </div>
              <Controller
                name="city"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <Input
                    label={t('common.city')}
                    placeholder={t('common.city')}
                    id="city"
                    error={errors.city?.message}
                    name={name}
                    disabled={disabled}
                    type="string"
                    required
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <Input
                    label={t('common.phone')}
                    error={errors.phoneNumber?.message}
                    placeholder={t('common.placeholder.phone')}
                    id="phone-number"
                    name={name}
                    disabled={disabled}
                    type="string"
                    required
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
              <div className="d-flex">
                <Controller
                  name="primaryContactFirstName"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Input
                      className="w-100 pr-3"
                      label={t('modal.manageExternalUserModal.form.contactFirstName')}
                      placeholder={t('modal.manageExternalUserModal.form.placeholder.name')}
                      error={errors.primaryContactFirstName?.message}
                      id="contact-first-name"
                      name={name}
                      disabled={disabled}
                      type="string"
                      required={!isEditExternal}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                />
                <Controller
                  name="primaryContactLastName"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Input
                      className="w-100"
                      label={t('modal.manageExternalUserModal.form.contactLastName')}
                      placeholder={t('modal.manageExternalUserModal.form.placeholder.lastName')}
                      id="contact-last-name"
                      error={errors.primaryContactLastName?.message}
                      name={name}
                      disabled={disabled}
                      type="string"
                      required={!isEditExternal}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                />
              </div>
              <Controller
                name="primaryContactPhoneNumber"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <Input
                    label={t('modal.manageExternalUserModal.form.contactPhone')}
                    placeholder={t('common.placeholder.phone')}
                    error={errors.primaryContactPhoneNumber?.message}
                    id="contact-phone"
                    name={name}
                    disabled={disabled}
                    type="string"
                    required={!isEditExternal}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
            </>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default ManageExternalUserModal;
